import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faImage, faVideo, faArrowLeft, faArrowRight, faRocket,
  faCheckCircle, faSpinner, faUsers, faFilm,
} from "@fortawesome/free-solid-svg-icons";
import { getRequest, postRequest } from "services/api/apiService";
import { toast } from "react-toastify";
import { labelsForSlug } from "constants/platforms";

const CALL_TO_ACTIONS = [
  "LEARN_MORE", "SHOP_NOW", "SIGN_UP", "BOOK_NOW",
  "CONTACT_US", "DOWNLOAD", "GET_OFFER", "WATCH_MORE",
];

const cleanName = (name) =>
  (name || "library-asset")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-zA-Z0-9]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .toLowerCase()
    .slice(0, 50);

const STEPS = ["Creative", "Audience", "Launch"];

const CreateAdFromLibrary = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const asset = state?.asset;

  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [connectedPlatforms, setConnectedPlatforms] = useState([]);
  const [adAccounts, setAdAccounts] = useState({});

  const baseName = cleanName(asset?.name || "library-asset");

  const [creative, setCreative] = useState({
    adTitle: asset?.name || "",
    adText: "",
    description: "",
    callToAction: "LEARN_MORE",
    destinationUrl: "",
  });

  const [audience, setAudience] = useState({
    platforms: [],
    adAccountMeta: "",
    adAccountTikTok: "",
    adAccountSnapchat: "",
    adAccountLinkedIn: "",
    adAccountGoogle: "",
    gender: ["All"],
    ageMin: "18",
    ageMax: "65",
    location: ["US"],
    budgetMeta: 10,
    budgetTikTok: 10,
    budgetGoogle: 10,
    budgetSnapchat: 10,
    budgetLinkedIn: 10,
    startTime: new Date().toISOString().slice(0, 10),
    endTime: "",
  });

  useEffect(() => {
    if (!asset) { navigate("/dashboard/ad-library"); return; }
    getRequest("/api/ad-accounts")
      .then((accounts) => {
        if (!Array.isArray(accounts)) return;
        const platforms = [...new Set(accounts.map((a) => a.platform).filter(Boolean))];
        setConnectedPlatforms(platforms);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const selected = audience.platforms;
    if (selected.includes("Facebook") || selected.includes("Instagram")) {
      getRequest("/api/ads/meta/ad-accounts").then((res) => {
        const list = (res?.adAccounts || []).map((a) => ({ value: a.adAccountId, label: `${a.name} (${a.adAccountId})` }));
        setAdAccounts((p) => ({ ...p, meta: list }));
        if (list.length && !audience.adAccountMeta) {
          setAudience((p) => ({ ...p, adAccountMeta: list[0].value }));
        }
      }).catch(() => {});
    }
    if (selected.includes("TikTok")) {
      getRequest("/api/ads/tiktok/fetch-tiktok-ads-account").then((res) => {
        const ids = res?.data?.advertiser_ids || [];
        const list = ids.map((id) => ({ value: id, label: `Advertiser ${id}` }));
        setAdAccounts((p) => ({ ...p, tiktok: list }));
        if (list.length && !audience.adAccountTikTok) setAudience((p) => ({ ...p, adAccountTikTok: list[0].value }));
      }).catch(() => {});
    }
    if (selected.includes("Snapchat")) {
      getRequest("/api/ads/snapchat/fetch-available-ad-accounts").then((res) => {
        const list = (res?.data || []).map((a) => ({ value: a.id, label: `${a.name || a.id} (${a.id})` }));
        setAdAccounts((p) => ({ ...p, snapchat: list }));
        if (list.length && !audience.adAccountSnapchat) setAudience((p) => ({ ...p, adAccountSnapchat: list[0].value }));
      }).catch(() => {});
    }
    if (selected.includes("LinkedIn")) {
      getRequest("/api/ads/linkedin/ad-accounts").then((res) => {
        const list = (res?.data?.accounts || []).map((a) => ({ value: a.id, label: `${a.name} (${a.id})` }));
        setAdAccounts((p) => ({ ...p, linkedin: list }));
        if (list.length && !audience.adAccountLinkedIn) setAudience((p) => ({ ...p, adAccountLinkedIn: list[0].value }));
      }).catch(() => {});
    }
    if (selected.includes("Google")) {
      getRequest("/api/ads/google/fetch-accessible-ads-accounts").then((res) => {
        const list = (res?.data || []).map((a) => ({ value: a.id, label: `Account ${a.id}` }));
        setAdAccounts((p) => ({ ...p, google: list }));
        if (list.length && !audience.adAccountGoogle) setAudience((p) => ({ ...p, adAccountGoogle: list[0].value }));
      }).catch(() => {});
    }
  }, [audience.platforms]);

  const togglePlatform = (platform) => {
    setAudience((p) => ({
      ...p,
      platforms: p.platforms.includes(platform)
        ? p.platforms.filter((x) => x !== platform)
        : [...p.platforms, platform],
    }));
  };

  const buildFormData = () => {
    const creativeType = asset.type === "video" ? "Video" : "Image";
    return {
      name: baseName,
      adSetName: baseName,
      adCreativeName: `${baseName}-creative`,
      objective: "OUTCOME_TRAFFIC",
      status: "ACTIVE",
      bid_strategy: "LOWEST_COST",
      publisherPlatform: audience.platforms,
      creativeType,
      imageUrl: creativeType === "Image" ? asset.url : "",
      videoUrl: creativeType === "Video" ? asset.url : "",
      adTitle: creative.adTitle,
      adText: creative.adText,
      description: creative.description,
      callToAction: creative.callToAction,
      destinationUrl: creative.destinationUrl,
      gender: audience.gender,
      age: [audience.ageMin, audience.ageMax],
      location: audience.location,
      startTime: audience.startTime,
      endTime: audience.endTime,
      adAccountMeta: audience.adAccountMeta,
      adAccountTikTok: audience.adAccountTikTok,
      adAccountSnapchat: audience.adAccountSnapchat,
      adAccountLinkedIn: audience.adAccountLinkedIn,
      adAccountGoogle: audience.adAccountGoogle,
      budgetMeta: audience.budgetMeta,
      budgetTikTok: audience.budgetTikTok,
      budgetGoogle: audience.budgetGoogle,
      budgetSnapchat: audience.budgetSnapchat,
      budgetLinkedIn: audience.budgetLinkedIn,
      budgetTypeMeta: "Daily", currencyMeta: "MAD",
      budgetTypeTikTok: "Daily", currencyTikTok: "MAD",
      budgetTypeGoogle: "Daily", currencyGoogle: "MAD",
      budgetTypeSnapchat: "Daily", currencySnapchat: "MAD",
      budgetTypeLinkedIn: "Daily", currencyLinkedIn: "MAD",
      interests: [],
      placements: [],
    };
  };

  const handleLaunch = async () => {
    if (!audience.platforms.length) { toast.error("Select at least one platform."); return; }
    if (!creative.destinationUrl) { toast.error("Destination URL is required."); return; }
    setSubmitting(true);
    const formData = buildFormData();
    const platforms = audience.platforms.map((p) => p.toLowerCase());
    let success = 0;
    try {
      if (platforms.includes("facebook") || platforms.includes("instagram")) {
        const r = await postRequest("/api/ads/meta/create-campaign", formData);
        if (r?.data) { toast.success(`Meta campaign created! ID: ${r.data.campaignId}`); success++; }
      }
      if (platforms.includes("tiktok")) {
        const r = await postRequest("/api/ads/tiktok/create-campaign", formData);
        if (r?.data) { toast.success(`TikTok campaign created! ID: ${r.data.campaignId}`); success++; }
      }
      if (platforms.includes("google")) {
        const r = await postRequest("/api/ads/google/create-campaign", formData);
        if (r?.data) { toast.success(`Google campaign created! ID: ${r.data.campaignId}`); success++; }
      }
      if (platforms.includes("snapchat")) {
        const r = await postRequest("/api/ads/snapchat/create-campaign", formData);
        if (r?.data) { toast.success(`Snapchat campaign created! ID: ${r.data.campaignId}`); success++; }
      }
      if (platforms.includes("linkedin")) {
        const r = await postRequest("/api/ads/linkedin/create-campaign", formData);
        if (r?.data) { toast.success(`LinkedIn campaign created! ID: ${r.data.campaignId}`); success++; }
      }
      if (success > 0) {
        setTimeout(() => navigate("/dashboard/campaigns"), 1500);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Campaign creation failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const canNext = () => {
    if (step === 0) return !!(creative.adTitle && creative.adText && creative.callToAction);
    if (step === 1) return audience.platforms.length > 0 && !!creative.destinationUrl;
    return true;
  };

  if (!asset) return null;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6 shrink-0">
        <button
          onClick={() => navigate("/dashboard/ad-library")}
          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 transition-all"
        >
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#221C51] to-[#4A3D9A] flex items-center justify-center shadow">
          <FontAwesomeIcon icon={faRocket} className="text-white text-sm" />
        </div>
        <div>
          <h1 className="text-lg font-bold text-[#221C51] leading-tight">Create Ad from Library</h1>
          <p className="text-xs text-gray-400 truncate max-w-xs">{asset.name || "Unnamed asset"}</p>
        </div>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-2 mb-6 shrink-0">
        {STEPS.map((label, i) => (
          <React.Fragment key={label}>
            <div className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full transition-all ${
              i === step ? "bg-[#221C51] text-white" :
              i < step  ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-400"
            }`}>
              {i < step && <FontAwesomeIcon icon={faCheckCircle} className="text-[10px]" />}
              {label}
            </div>
            {i < STEPS.length - 1 && (
              <div className={`flex-1 h-0.5 rounded-full ${i < step ? "bg-green-300" : "bg-gray-200"}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">

        {/* ── Step 0: Creative ── */}
        {step === 0 && (
          <div className="flex gap-6 max-w-3xl">
            {/* Asset preview */}
            <div className="shrink-0 w-40">
              <div className="w-40 h-40 rounded-2xl overflow-hidden bg-gray-100 border border-gray-200 shadow-sm">
                {asset.type === "video"
                  ? <video src={asset.url} className="w-full h-full object-cover" muted />
                  : <img src={asset.url} alt={asset.name} className="w-full h-full object-cover" />
                }
              </div>
              <div className={`mt-2 text-center text-[10px] font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${
                asset.type === "video" ? "bg-purple-100 text-purple-700" : "bg-blue-100 text-blue-700"
              }`}>
                <FontAwesomeIcon icon={asset.type === "video" ? faVideo : faImage} className="text-[8px]" />
                {asset.type === "video" ? "Video" : "Image"}
              </div>
            </div>

            {/* Creative fields */}
            <div className="flex-1 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-[#221C51] mb-1">Ad Headline *</label>
                <input
                  value={creative.adTitle}
                  onChange={(e) => setCreative((p) => ({ ...p, adTitle: e.target.value }))}
                  placeholder="Short, catchy headline"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#221C51] bg-gray-50"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#221C51] mb-1">Body Text *</label>
                <textarea
                  value={creative.adText}
                  onChange={(e) => setCreative((p) => ({ ...p, adText: e.target.value }))}
                  placeholder="Describe your offer…"
                  rows={3}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#221C51] bg-gray-50 resize-none"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#221C51] mb-1">Description</label>
                <input
                  value={creative.description}
                  onChange={(e) => setCreative((p) => ({ ...p, description: e.target.value }))}
                  placeholder="Optional extra context"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#221C51] bg-gray-50"
                />
              </div>
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="block text-xs font-semibold text-[#221C51] mb-1">Call to Action *</label>
                  <select
                    value={creative.callToAction}
                    onChange={(e) => setCreative((p) => ({ ...p, callToAction: e.target.value }))}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm bg-gray-50 focus:outline-none focus:border-[#221C51]"
                  >
                    {CALL_TO_ACTIONS.map((c) => (
                      <option key={c} value={c}>{c.replace(/_/g, " ")}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Step 1: Audience ── */}
        {step === 1 && (
          <div className="max-w-2xl space-y-5">
            {/* Destination URL */}
            <div>
              <label className="block text-xs font-semibold text-[#221C51] mb-1">Destination URL *</label>
              <input
                value={creative.destinationUrl}
                onChange={(e) => setCreative((p) => ({ ...p, destinationUrl: e.target.value }))}
                placeholder="https://yoursite.com/landing"
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#221C51] bg-gray-50"
              />
            </div>

            {/* Platforms */}
            <div>
              <label className="block text-xs font-semibold text-[#221C51] mb-2">
                <FontAwesomeIcon icon={faUsers} className="mr-1" /> Platforms *
              </label>
              <div className="flex flex-wrap gap-2">
                {connectedPlatforms.map((p) => {
                  const labels = labelsForSlug(p);
                  return labels.map((label) => (
                    <button key={label} onClick={() => togglePlatform(label)}
                      className={`text-xs px-3 py-1.5 rounded-full border font-semibold transition-all ${
                        audience.platforms.includes(label)
                          ? "bg-[#221C51] text-white border-[#221C51]"
                          : "bg-white text-gray-500 border-gray-200 hover:border-gray-400"
                      }`}>
                      {label}
                    </button>
                  ));
                })}
                {connectedPlatforms.length === 0 && (
                  <p className="text-xs text-gray-400">No connected platforms found. Go to Platforms to connect.</p>
                )}
              </div>
            </div>

            {/* Ad Account per platform */}
            {(audience.platforms.includes("Facebook") || audience.platforms.includes("Instagram")) && adAccounts.meta?.length > 0 && (
              <div>
                <label className="block text-xs font-semibold text-[#221C51] mb-1">Meta Ad Account</label>
                <select value={audience.adAccountMeta} onChange={(e) => setAudience((p) => ({ ...p, adAccountMeta: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm bg-gray-50 focus:outline-none focus:border-[#221C51]">
                  {adAccounts.meta.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                </select>
              </div>
            )}
            {audience.platforms.includes("TikTok") && adAccounts.tiktok?.length > 0 && (
              <div>
                <label className="block text-xs font-semibold text-[#221C51] mb-1">TikTok Advertiser</label>
                <select value={audience.adAccountTikTok} onChange={(e) => setAudience((p) => ({ ...p, adAccountTikTok: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm bg-gray-50 focus:outline-none focus:border-[#221C51]">
                  {adAccounts.tiktok.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                </select>
              </div>
            )}
            {audience.platforms.includes("LinkedIn") && adAccounts.linkedin?.length > 0 && (
              <div>
                <label className="block text-xs font-semibold text-[#221C51] mb-1">LinkedIn Ad Account</label>
                <select value={audience.adAccountLinkedIn} onChange={(e) => setAudience((p) => ({ ...p, adAccountLinkedIn: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm bg-gray-50 focus:outline-none focus:border-[#221C51]">
                  {adAccounts.linkedin.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                </select>
              </div>
            )}
            {audience.platforms.includes("Snapchat") && adAccounts.snapchat?.length > 0 && (
              <div>
                <label className="block text-xs font-semibold text-[#221C51] mb-1">Snapchat Ad Account</label>
                <select value={audience.adAccountSnapchat} onChange={(e) => setAudience((p) => ({ ...p, adAccountSnapchat: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm bg-gray-50 focus:outline-none focus:border-[#221C51]">
                  {adAccounts.snapchat.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                </select>
              </div>
            )}

            {/* Gender + Age */}
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="block text-xs font-semibold text-[#221C51] mb-1">Gender</label>
                <select value={audience.gender[0]} onChange={(e) => setAudience((p) => ({ ...p, gender: [e.target.value] }))}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm bg-gray-50 focus:outline-none focus:border-[#221C51]">
                  <option value="All">All</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </div>
              <div className="flex-1">
                <label className="block text-xs font-semibold text-[#221C51] mb-1">Age Min</label>
                <input type="number" min="13" max="65" value={audience.ageMin}
                  onChange={(e) => setAudience((p) => ({ ...p, ageMin: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm bg-gray-50 focus:outline-none focus:border-[#221C51]" />
              </div>
              <div className="flex-1">
                <label className="block text-xs font-semibold text-[#221C51] mb-1">Age Max</label>
                <input type="number" min="13" max="65" value={audience.ageMax}
                  onChange={(e) => setAudience((p) => ({ ...p, ageMax: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm bg-gray-50 focus:outline-none focus:border-[#221C51]" />
              </div>
            </div>

            {/* Location */}
            <div>
              <label className="block text-xs font-semibold text-[#221C51] mb-1">Location (country codes, comma-separated)</label>
              <input
                value={audience.location.join(", ")}
                onChange={(e) => setAudience((p) => ({ ...p, location: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) }))}
                placeholder="US, FR, MA"
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm bg-gray-50 focus:outline-none focus:border-[#221C51]"
              />
            </div>

            {/* Budget per platform */}
            <div>
              <label className="block text-xs font-semibold text-[#221C51] mb-2">Daily Budget (MAD) per platform</label>
              <div className="space-y-2">
                {audience.platforms.includes("Facebook") || audience.platforms.includes("Instagram") ? (
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-500 w-20">Meta</span>
                    <input type="number" min="0" value={audience.budgetMeta}
                      onChange={(e) => setAudience((p) => ({ ...p, budgetMeta: parseFloat(e.target.value) || 0 }))}
                      className="w-32 border border-gray-200 rounded-xl px-3 py-1.5 text-sm bg-gray-50 focus:outline-none focus:border-[#221C51]" />
                    <span className="text-xs text-gray-400">MAD / day</span>
                  </div>
                ) : null}
                {audience.platforms.includes("TikTok") && (
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-500 w-20">TikTok</span>
                    <input type="number" min="0" value={audience.budgetTikTok}
                      onChange={(e) => setAudience((p) => ({ ...p, budgetTikTok: parseFloat(e.target.value) || 0 }))}
                      className="w-32 border border-gray-200 rounded-xl px-3 py-1.5 text-sm bg-gray-50 focus:outline-none focus:border-[#221C51]" />
                    <span className="text-xs text-gray-400">MAD / day</span>
                  </div>
                )}
                {audience.platforms.includes("LinkedIn") && (
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-500 w-20">LinkedIn</span>
                    <input type="number" min="0" value={audience.budgetLinkedIn}
                      onChange={(e) => setAudience((p) => ({ ...p, budgetLinkedIn: parseFloat(e.target.value) || 0 }))}
                      className="w-32 border border-gray-200 rounded-xl px-3 py-1.5 text-sm bg-gray-50 focus:outline-none focus:border-[#221C51]" />
                    <span className="text-xs text-gray-400">MAD / day</span>
                  </div>
                )}
                {audience.platforms.includes("Snapchat") && (
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-500 w-20">Snapchat</span>
                    <input type="number" min="0" value={audience.budgetSnapchat}
                      onChange={(e) => setAudience((p) => ({ ...p, budgetSnapchat: parseFloat(e.target.value) || 0 }))}
                      className="w-32 border border-gray-200 rounded-xl px-3 py-1.5 text-sm bg-gray-50 focus:outline-none focus:border-[#221C51]" />
                    <span className="text-xs text-gray-400">MAD / day</span>
                  </div>
                )}
                {audience.platforms.includes("Google") && (
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-500 w-20">Google</span>
                    <input type="number" min="0" value={audience.budgetGoogle}
                      onChange={(e) => setAudience((p) => ({ ...p, budgetGoogle: parseFloat(e.target.value) || 0 }))}
                      className="w-32 border border-gray-200 rounded-xl px-3 py-1.5 text-sm bg-gray-50 focus:outline-none focus:border-[#221C51]" />
                    <span className="text-xs text-gray-400">MAD / day</span>
                  </div>
                )}
              </div>
            </div>

            {/* Dates */}
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="block text-xs font-semibold text-[#221C51] mb-1">Start Date *</label>
                <input type="date" value={audience.startTime}
                  min={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setAudience((p) => ({ ...p, startTime: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm bg-gray-50 focus:outline-none focus:border-[#221C51]" />
              </div>
              <div className="flex-1">
                <label className="block text-xs font-semibold text-[#221C51] mb-1">End Date (optional)</label>
                <input type="date" value={audience.endTime}
                  min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)}
                  onChange={(e) => setAudience((p) => ({ ...p, endTime: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm bg-gray-50 focus:outline-none focus:border-[#221C51]" />
              </div>
            </div>
          </div>
        )}

        {/* ── Step 2: Launch ── */}
        {step === 2 && (
          <div className="max-w-xl space-y-4">
            <div className="bg-[#221C51]/5 border border-[#221C51]/10 rounded-2xl p-4 space-y-3">
              <p className="text-xs font-bold text-[#221C51] uppercase tracking-wide mb-2">Auto-generated names</p>
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-400 w-24">Campaign</span>
                <span className="font-mono text-xs bg-white border border-gray-200 rounded-lg px-2 py-1 text-[#221C51]">{baseName}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-400 w-24">Ad Set</span>
                <span className="font-mono text-xs bg-white border border-gray-200 rounded-lg px-2 py-1 text-[#221C51]">{baseName}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-400 w-24">Creative</span>
                <span className="font-mono text-xs bg-white border border-gray-200 rounded-lg px-2 py-1 text-[#221C51]">{baseName}-creative</span>
              </div>
            </div>

            {/* Summary */}
            <div className="bg-white border border-gray-100 rounded-2xl p-4 space-y-2 shadow-sm">
              <p className="text-xs font-bold text-[#221C51] uppercase tracking-wide mb-2">Summary</p>
              <Row label="Asset" value={asset.name || "—"} />
              <Row label="Type" value={asset.type === "video" ? "Video" : "Image"} />
              <Row label="Headline" value={creative.adTitle || "—"} />
              <Row label="CTA" value={creative.callToAction.replace(/_/g, " ")} />
              <Row label="URL" value={creative.destinationUrl || "—"} />
              <Row label="Platforms" value={audience.platforms.join(", ") || "—"} />
              <Row label="Gender" value={audience.gender[0]} />
              <Row label="Age" value={`${audience.ageMin} – ${audience.ageMax}`} />
              <Row label="Locations" value={audience.location.join(", ")} />
              <Row label="Start" value={audience.startTime} />
            </div>

            <button
              onClick={handleLaunch}
              disabled={submitting}
              className="w-full flex items-center justify-center gap-2 bg-[#221C51] text-white text-sm font-bold py-3 rounded-2xl hover:bg-[#2d2566] transition-all disabled:opacity-50"
            >
              {submitting
                ? <><FontAwesomeIcon icon={faSpinner} spin /> Launching…</>
                : <><FontAwesomeIcon icon={faRocket} /> Launch Campaign</>
              }
            </button>
          </div>
        )}
      </div>

      {/* Navigation */}
      {step < 2 && (
        <div className="flex justify-between pt-4 border-t border-gray-100 shrink-0 mt-4">
          <button
            onClick={() => step > 0 ? setStep(step - 1) : navigate("/dashboard/ad-library")}
            className="flex items-center gap-2 text-sm text-gray-400 hover:text-[#221C51] transition-colors"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="text-xs" />
            {step === 0 ? "Back to library" : "Back"}
          </button>
          <button
            onClick={() => setStep(step + 1)}
            disabled={!canNext()}
            className="flex items-center gap-2 px-5 py-2 bg-[#221C51] text-white text-sm font-semibold rounded-xl hover:bg-[#2d2566] transition-all disabled:opacity-40"
          >
            Next
            <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
          </button>
        </div>
      )}
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex items-start gap-2">
    <span className="text-xs text-gray-400 w-20 shrink-0">{label}</span>
    <span className="text-xs text-[#221C51] font-medium break-all">{value}</span>
  </div>
);

export default CreateAdFromLibrary;
